import React, { useMemo } from 'react';
import styled from 'styled-components';

import { formatDate, parseLocalDate, todayDate } from 'utils';
import { FlyoutItem, IconDeleteLine, IconPencilOutline, Tag, TagTypes } from 'widgets';
import { FlyoutMenu } from 'widgets/Flyout';

export enum TimelineStatus {
    ACTIVE = 'ACTIVE',
    UPCOMING = 'UPCOMING',
    EXPIRED = 'EXPIRED',
}

const TAG_MAP: { [k in TimelineStatus]: TagTypes } = {
    [TimelineStatus.ACTIVE]: 'positive',
    [TimelineStatus.UPCOMING]: 'info',
    [TimelineStatus.EXPIRED]: 'neutral',
};

interface Props {
    firstColumn: JSX.Element;
    secondColumn: JSX.Element;
    status: TimelineStatus;
    startDate: DateStr;
    endDate?: DateStr;
    onEdit?: () => void;
    onDelete?: () => void;
}

export const TimelineCard = ({ firstColumn, secondColumn, status, startDate, endDate, onEdit, onDelete }: Props) => {
    const endDateText = useMemo(() => {
        if (!endDate) {
            return '';
        }
        return (parseLocalDate(endDate) < todayDate() ? 'Expired: ' : 'Expires: ') + formatDate(endDate);
    }, [endDate]);

    return (
        <Card $status={status}>
            <ContentRow>
                <Column>{firstColumn}</Column>
                <Column>{secondColumn}</Column>
                <EndColumn>
                    <Tag type={TAG_MAP[status]}>{status}</Tag>
                </EndColumn>
            </ContentRow>
            <DateRow>
                <Column>Start: {formatDate(startDate)}</Column>
                <Column>{endDateText}</Column>
                <EndColumn>
                    {(onEdit || onDelete) && (
                        <StyledFlyoutMenu>
                            <FlyoutItem onClick={onEdit} disabled={!onEdit} icon={<IconPencilOutline size="micro" />}>
                                Edit
                            </FlyoutItem>
                            <FlyoutItem
                                onClick={onDelete}
                                color="negative"
                                disabled={!onDelete}
                                icon={<IconDeleteLine size="micro" />}
                            >
                                Delete
                            </FlyoutItem>
                        </StyledFlyoutMenu>
                    )}
                </EndColumn>
            </DateRow>
        </Card>
    );
};

const StyledFlyoutMenu = styled(FlyoutMenu)`
    margin-left: 0.5rem;
`;

const Card = styled.div<{ $status: TimelineStatus }>`
    display: flex;
    flex-direction: column;
    min-height: 120px;
    margin-bottom: 16px;
    padding: 16px 32px;
    border: 1px solid
        ${({ theme, $status }) => ($status === TimelineStatus.ACTIVE ? theme.border.highlight : theme.border.light)};
    border-radius: 3px;
    background-color: ${({ theme, $status }) =>
        $status === TimelineStatus.ACTIVE && theme.container.highlightBackground};
`;

const ContentRow = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
`;

const DateRow = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
    color: ${({ theme }) => theme.text.subtle};
`;

const Column = styled.div`
    flex: 2;
`;

const EndColumn = styled.div`
    display: flex;
    justify-content: end;
    flex: 1;
`;
